import { useEffect, useState, useMemo, useRef } from "react";
import { toast } from "sonner";
import { MapPin, FloppyDisk, FilePdf, Crosshair } from "@phosphor-icons/react";
import { MapContainer, TileLayer, Marker, Circle, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// Fix default marker icons for CRA (Leaflet expects images at /marker-icon.png).
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Default fallback center: CDMX
const DEFAULT_CENTER = [19.4326, -99.1332];

function ClickHandler({ onPick }) {
  useMapEvents({
    click(e) { onPick(e.latlng.lat, e.latlng.lng); },
  });
  return null;
}

function Recenter({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, map.getZoom());
  }, [center, map]);
  return null;
}

/**
 * WorksiteGeofencePanel — configura el pin y radio de la geocerca del sitio
 * de obra. Reemplaza el PUT crudo por un mapa Leaflet visual.
 * También incluye botón para descargar el reporte semanal de fichados fuera
 * del radio (auditoría SGSSO).
 */
export default function WorksiteGeofencePanel() {
  const [lat, setLat] = useState(DEFAULT_CENTER[0]);
  const [lng, setLng] = useState(DEFAULT_CENTER[1]);
  const [radius, setRadius] = useState(500);
  const [configured, setConfigured] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const markerRef = useRef(null);

  useEffect(() => {
    api.get("/settings/worksite-geofence").then((r) => {
      if (r.data.configured) {
        setLat(r.data.lat);
        setLng(r.data.lng);
        setConfigured(true);
      }
      setRadius(r.data.radius_m || 500);
    }).catch(() => {});
  }, []);

  const center = useMemo(() => [Number(lat) || DEFAULT_CENTER[0], Number(lng) || DEFAULT_CENTER[1]], [lat, lng]);

  const pickLocation = (nlat, nlng) => {
    setLat(Number(nlat.toFixed(6)));
    setLng(Number(nlng.toFixed(6)));
  };

  const locateMe = () => {
    if (!navigator.geolocation) {
      toast.error("Tu navegador no soporta geolocalización");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => pickLocation(pos.coords.latitude, pos.coords.longitude),
      () => toast.error("No pudimos obtener tu ubicación"),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const save = async () => {
    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) {
      toast.error("Coordenadas fuera de rango");
      return;
    }
    if (radius < 10 || radius > 50000) {
      toast.error("Radio válido: 10 a 50000 m");
      return;
    }
    setSaving(true);
    try {
      await api.put("/settings/worksite-geofence", {
        lat: Number(lat), lng: Number(lng), radius_m: Number(radius),
      });
      setConfigured(true);
      toast.success("Geocerca guardada");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const download7Days = async () => {
    setDownloading(true);
    try {
      const to = new Date();
      const from = new Date();
      from.setDate(from.getDate() - 6);
      const iso = (d) => d.toISOString().slice(0, 10);
      const r = await api.get("/hr/geofence-outside/pdf", {
        params: { date_from: iso(from), date_to: iso(to) },
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `geocerca-fuera-${iso(from)}-a-${iso(to)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Reporte descargado");
    } catch (e) { toast.error(apiError(e)); }
    finally { setDownloading(false); }
  };

  return (
    <div className="mt-6 border border-slate-200 rounded-lg p-5 bg-white" data-testid="geofence-panel">
      <div className="flex items-center gap-2 mb-1">
        <MapPin size={18} weight="fill" className="text-red-600" />
        <h2 className="font-heading font-black text-slate-900">Geocerca del sitio</h2>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Define el pin de la obra y el radio (m). Los fichados por WhatsApp fuera del radio
        aparecerán marcados con <b>⚠️ fuera</b> tanto para el operador como en la auditoría SGSSO.
      </p>

      <div className="rounded-md overflow-hidden border border-slate-300 mb-3" style={{ height: 320 }}>
        <MapContainer center={center} zoom={configured ? 16 : 12} style={{ height: "100%", width: "100%" }}
                       data-testid="geofence-map">
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <ClickHandler onPick={pickLocation} />
          <Recenter center={center} />
          <Marker position={center}
                   draggable={true}
                   ref={markerRef}
                   eventHandlers={{
                     dragend: () => {
                       const m = markerRef.current;
                       if (m) {
                         const p = m.getLatLng();
                         pickLocation(p.lat, p.lng);
                       }
                     },
                   }} />
          <Circle center={center} radius={Number(radius) || 0}
                   pathOptions={{ color: "#DC2626", fillColor: "#FCA5A5", fillOpacity: 0.25 }} />
        </MapContainer>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <div>
          <Label className="overline-label">Latitud</Label>
          <Input type="number" step="0.000001" value={lat}
                  onChange={(e) => setLat(e.target.value)}
                  data-testid="geofence-lat" className="h-9 font-mono text-xs" />
        </div>
        <div>
          <Label className="overline-label">Longitud</Label>
          <Input type="number" step="0.000001" value={lng}
                  onChange={(e) => setLng(e.target.value)}
                  data-testid="geofence-lng" className="h-9 font-mono text-xs" />
        </div>
        <div>
          <Label className="overline-label">Radio (m)</Label>
          <Input type="number" min={10} max={50000} step={10} value={radius}
                  onChange={(e) => setRadius(e.target.value)}
                  data-testid="geofence-radius" className="h-9" />
        </div>
        <div className="flex items-end">
          <Button variant="outline" onClick={locateMe} className="w-full gap-1.5" data-testid="geofence-locate">
            <Crosshair size={14} weight="bold" /> Usar mi ubicación
          </Button>
        </div>
      </div>

      <input type="range" min={20} max={5000} step={10} value={radius}
              onChange={(e) => setRadius(e.target.value)}
              data-testid="geofence-radius-slider"
              className="w-full mb-4" />

      <div className="flex flex-wrap gap-2">
        <Button onClick={save} disabled={saving}
                 className="bg-blue-900 hover:bg-blue-800 gap-1.5" data-testid="geofence-save">
          <FloppyDisk size={14} weight="bold" /> {saving ? "Guardando…" : "Guardar geocerca"}
        </Button>
        <Button variant="outline" onClick={download7Days} disabled={downloading}
                 className="border-amber-600 text-amber-700 hover:bg-amber-50 gap-1.5"
                 data-testid="geofence-report-btn">
          <FilePdf size={14} weight="bold" /> {downloading ? "Generando…" : "Reporte semanal · fichados fuera"}
        </Button>
      </div>
      <p className="text-[10px] text-slate-400 mt-2">
        Tip: haz click sobre el mapa o arrastra el pin para elegir el punto exacto.
        El reporte semanal cubre los últimos 7 días y lista personas + distancia + hora.
      </p>
    </div>
  );
}
